import {IGroupSavingsState} from "../types";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {toAmount} from "../summary";
import {adminRequest, buildQuery, getReturnedObject} from "../../api/adminApi";

const initialState: IGroupSavingsState = {
    groupSavings: null,
    selectedGroupSavings: null,
    selectedCategory: null,
    loading: false,
    detailLoading: false,
    error: undefined,
    pageInfo: {
        pageNumber: 0,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0
    },
    summary: {
        totalCount: 0,
        activeCount: 0,
        totalMembers: 0,
        totalCategories: 0,
        totalContributed: 0,
        totalBookBalance: 0,
        totalActiveBalance: 0
    }
};

export const getGroupSavings = createAsyncThunk<any, {
    token: string,
    searchParam: string,
    pageNumber: number
}>("getGroupSavings", async (data) => {
    return adminRequest(`/group-savings/report/all${buildQuery({
        searchParam: data.searchParam,
        pageSize: 10,
        pageNumber: data.pageNumber
    })}`, {token: data.token});
});

export const getGroupSavingsDetails = createAsyncThunk<any, {
    token: string,
    groupId: string
}>("getGroupSavingsDetails", async (data) => {
    return adminRequest(`/group-savings/${data.groupId}`, {token: data.token});
});

export const getGroupSavingsCategoryDetails = createAsyncThunk<any, {
    token: string,
    categoryId: string
}>("getGroupSavingsCategoryDetails", async (data) => {
    return adminRequest(`/group-savings/categories/${data.categoryId}`, {token: data.token});
});

export const updateGroupSavingsLiquidationEmailSetting = createAsyncThunk<any, {
    token: string,
    groupId: string,
    allUserLiquidationRequestEmail: boolean
}>("updateGroupSavingsLiquidationEmailSetting", async (data) => {
    return adminRequest(`/group-savings/${data.groupId}/liquidation-email-setting`, {
        method: "PATCH",
        token: data.token,
        body: JSON.stringify({
            allUserLiquidationRequestEmail: data.allUserLiquidationRequestEmail
        })
    });
});

const groupSavingsSlice = createSlice({
    name: 'groupSavings',
    initialState,
    reducers: {
        reset: (state) => {
            state.groupSavings = null;
            state.selectedGroupSavings = null;
            state.selectedCategory = null;
            state.loading = false;
            state.detailLoading = false;
            state.error = undefined;
            state.pageInfo = {
                pageNumber: 0,
                pageSize: 0,
                totalItems: 0,
                totalPages: 0
            };
            state.summary = {
                totalCount: 0,
                activeCount: 0,
                totalMembers: 0,
                totalCategories: 0,
                totalContributed: 0,
                totalBookBalance: 0,
                totalActiveBalance: 0
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getGroupSavings.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(getGroupSavings.fulfilled, (state, action) => {
                if (!action.payload) return;

                if (action.payload.isSuccessfull === false) {
                    state.loading = false;
                    state.error = action.payload.message;
                    return;
                }

                state.loading = false;
                state.error = undefined;
                const returnedObject = getReturnedObject(action.payload);
                const savings = returnedObject?.savings || returnedObject?.Savings || [];
                state.groupSavings = savings.map((item: any) => {
                    return {...item, key: item.id};
                });
                const pageInfo = returnedObject?.pageInfo || returnedObject?.PageInfo || {};
                state.pageInfo = {
                    pageNumber: pageInfo.pageNumber ?? pageInfo.PageNumber ?? 1,
                    pageSize: pageInfo.pageSize ?? pageInfo.PageSize ?? 10,
                    totalItems: pageInfo.totalItems ?? pageInfo.TotalItems ?? savings.length,
                    totalPages: pageInfo.totalPages ?? pageInfo.TotalPages ?? 1,
                };
                state.summary.totalCount = state.pageInfo?.totalItems || savings.length;
                state.summary.activeCount = savings.filter((item: any) => {
                    return String(item.status || "").toLowerCase().includes("active");
                }).length;
                state.summary.totalBookBalance = returnedObject?.summary?.totalBookBalance ?? savings.reduce((total: number, item: any) => {
                    return total + toAmount(item.bookBalance);
                }, 0);
                state.summary.totalActiveBalance = returnedObject?.summary?.totalActiveBalance ?? savings.reduce((total: number, item: any) => {
                    return total + toAmount(item.activeBalance);
                }, 0);
                state.summary.totalMembers = returnedObject?.summary?.totalMembers || 0;
                state.summary.totalCategories = returnedObject?.summary?.totalCategories || 0;
                state.summary.totalContributed = returnedObject?.summary?.totalContributed || savings.reduce((total: number, item: any) => {
                    return total + toAmount(item.totalContributed);
                }, 0);
            })
            .addCase(getGroupSavings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(getGroupSavingsDetails.pending, (state) => {
                state.detailLoading = true;
                state.error = undefined;
                state.selectedGroupSavings = null;
            })
            .addCase(getGroupSavingsDetails.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.selectedGroupSavings = getReturnedObject(action.payload);
            })
            .addCase(getGroupSavingsDetails.rejected, (state, action) => {
                state.detailLoading = false;
                state.error = action.error.message;
            })
            .addCase(getGroupSavingsCategoryDetails.pending, (state) => {
                state.detailLoading = true;
                state.error = undefined;
                state.selectedCategory = null;
            })
            .addCase(getGroupSavingsCategoryDetails.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.selectedCategory = getReturnedObject(action.payload);
            })
            .addCase(getGroupSavingsCategoryDetails.rejected, (state, action) => {
                state.detailLoading = false;
                state.error = action.error.message;
            })
            .addCase(updateGroupSavingsLiquidationEmailSetting.fulfilled, (state, action) => {
                const returnedObject = getReturnedObject(action.payload);
                if (state.selectedGroupSavings) {
                    state.selectedGroupSavings.allUserLiquidationRequestEmail = returnedObject?.allUserLiquidationRequestEmail ?? false;
                }
            });
    }
});

export const {reset} = groupSavingsSlice.actions;

export default groupSavingsSlice.reducer;
